import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';
import Icon from '_components/atomic/Icon';
import Accordion from '_components/atomic/Accordion';
import { FAQ } from '_constants/faq';
import Input from '_components/atomic/Input';
import Button from '_components/atomic/Button';
import slice from 'lodash.slice';

const { colors, devices, fontSizes, spaces } = theme;

const StyledFAQ = styled.div`
  width: 750px;
  margin-left: auto;
  margin-right: auto;

  h1 {
    font-family: 'Aeonik';
    font-style: normal;
    font-weight: 500;
    font-size: ${fontSizes.large11X}px;
    line-height: 130%;
    text-align: center;
    color: ${colors.gray[800]};
  }

  .underHeader {
    font-family: 'Aeonik';
    font-style: normal;
    font-weight: 400;
    font-size: ${fontSizes.large}px;
    line-height: 150%;
    text-align: center;
    color: ${colors.gray[700]};
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
    margin-bottom: ${spaces.large6X + 2}px;
  }

  .faqContainer {
    background: ${colors.white};
    border-radius: 30px;
    padding: ${spaces.largeX + 2}px;

    .faqDetails {
      text-align: left;
    }
  }

  .loadMore {
    font-family: 'Aeonik';
    font-style: normal;
    font-weight: 500;
    font-size: ${fontSizes.large}px;
    line-height: 150%;
    margin-top: ${spaces.medium}px;
    color: ${colors.blue[500]};
    text-align: center;
    cursor: pointer;
  }

  .searchContainer {
    padding-bottom: ${spaces.largeX + 2}px;
  }

  @media ${devices.tablet} {
    width: 100%;
    padding-left: ${spaces.regular}px;
    padding-right: ${spaces.regular}px;
    .faqContainer {
      padding: 0px;

      .faqTitle {
        font-family: 'Aeonik';
        font-style: normal;
        font-weight: 500;
        font-size: ${fontSizes.regular}px;
        line-height: 150%;
      }

      .faqDetails {
        font-family: 'Aeonik';
        font-style: normal;
        font-weight: 400;
        font-size: ${fontSizes.regular}px;
        line-height: 150%;
      }
    }

    .searchContainer {
      padding-top: ${spaces.regular}px;
      padding-bottom: ${spaces.regular}px;
    }

    .loadMore {
      font-size: ${fontSizes.regular}px;
    }
  }
`;

const Tab = styled.button`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: ${spaces.small}px ${spaces.large}px;
  margin-right: ${spaces.small - 2}px;
  height: 54px;
  font-family: 'Aeonik';
  font-style: normal;
  font-weight: 400;
  font-size: ${fontSizes.large}px;
  line-height: 150%;
  color: ${({ active }) => (active ? colors.white : colors.gray[500])};
  background: ${({ active }) => (active ? colors.blue[500] : colors.gray[50])};
  border-radius: ${spaces.small - 2}px ${spaces.small - 2}px 0px 0px;
  border-width: 0px;

  @media ${devices.tablet} {
    margin-right: 0px;
    width: 100%;
    border-radius: ${spaces.smallX}px;
    margin-bottom: ${spaces.small - 2}px;
    font-size: ${fontSizes.regular}px;
    justify-content: center;
    background: ${({ active }) =>
      active ? colors.blue[500] : colors.gray[100]};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;

  @media ${devices.tablet} {
    display: block;
  }
`;

const FAQSection = ({ withoutHeader }) => {
  const [activeSectionTitle, setActiveSectionTitle] = useState('');
  const [activeSectionItems, setActiveSectionItems] = useState([]);

  const [isCompleted, setIsCompleted] = useState(false);
  const [index, setIndex] = useState(5);
  const initialFaqs = slice(activeSectionItems, 0, index);

  const loadMore = () => {
    setIndex(activeSectionItems.length);
    setIsCompleted(true);
  };
  useEffect(() => {
    setActiveSectionTitle(FAQ[0].section);
    setActiveSectionItems(FAQ[0].items);
    setIndex(5);
    setIsCompleted(false);
  }, []);
  return (
    <StyledFAQ>
      {!withoutHeader && (
        <>
          <h1>FAQs</h1>
          <div className="underHeader">
            Everything you need to know about the trademark.
            <br /> Can&#39;t find the answer you&#39;re looking for? Chat with
            our friendly team!
          </div>
        </>
      )}
      <ButtonGroup>
        {FAQ.map((faqSection, idx) => (
          <Tab
            key={idx}
            active={activeSectionTitle === faqSection.section}
            onClick={(e) => {
              e.preventDefault();
              setActiveSectionTitle(faqSection.section);
              setActiveSectionItems(faqSection.items);
              setIndex(5);
              setIsCompleted(false);
            }}
          >
            {faqSection.section}
          </Tab>
        ))}
      </ButtonGroup>
      <div className="faqContainer">
        <div className="searchContainer">
          <Input
            borderColor={theme.colors.gray[200]}
            textColor={theme.colors.gray[500]}
            color={theme.colors.white}
            hoverColor={theme.colors.white}
            hoverTextColor={theme.colors.gray[500]}
            placeholder="Search your question"
            suffix={<Button color={theme.colors.blue[500]}>Search</Button>}
          />
        </div>
        <Accordion
          faqList
          openIcon={
            <Icon alias={ICONS_ALIASES.minus} color={theme.colors.gray[500]} />
          }
          closeIcon={
            <Icon alias={ICONS_ALIASES.plus} color={theme.colors.blue[500]} />
          }
          items={initialFaqs}
        />
        {!isCompleted && (
          <div className="loadMore" onClick={loadMore}>
            Load more{' '}
            <Icon alias={ICONS_ALIASES.more} color={theme.colors.blue[500]} />
          </div>
        )}
      </div>
    </StyledFAQ>
  );
};

FAQSection.propTypes = {
  withoutHeader: PropTypes.bool,
};

export default FAQSection;
